import { Alert, Box } from "@mui/material";
import * as React from "react";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult, DatabaseRecordType, TaskLockingType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import * as attachmentService from "../../../models/services/attachmentService";
import * as taskService from "../../../models/services/taskService";
import { Dialog } from "../../framework/Dialog/Dialog";
import { DialogItemsNav } from "../../framework/Dialog/DialogItemsNav";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { TaskDynamicFormContainer } from "../TaskDynamicFormContainer";
import { TaskDynamicForm } from "../TaskDynamicForm/TaskDynamicForm";
import { AttachmentList } from "source/components/attachments/AttachmentList/AttachmentList";
import { ITaskDataRowCol, TaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { IModifyUser, ModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { ISessionState } from "../../../models/session/sessionState";
import { FileAttachment, IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { ITaskDataRow, SaveTaskDataRow, TaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import downloadjs from "downloadjs";
import { SaveAttachmentBase64 } from "ui/Scripts/source/models/common/saveAttachmentBase64";
import { EditItem } from "ui/Scripts/source/models/common/editItem";
import { LockedRecord } from "ui/Scripts/source/models/common/lockedRecord";
import { recordLockManager } from "source/models/services/recordLockService";
import { StoreUtils } from "source/models/store/storeUtils";
import { Base } from "ui/Scripts/source/framework/base";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { ITaskTypeCol, TaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";

export interface ITaskDataRowDialogProp {
    sessionState: ISessionState;
    isReadOnly: boolean;
    companyId: string;
    taskId: string;
    id: string;
    taskType: ITaskEditTypeItem;
    hasPrevious: boolean;
    hasNext: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onSaved: () => void;
    onClose: (refreshList: boolean) => void;
    onRemove: (id: string) => Promise<ApiSuccess>;
    onPrint: () => void;
    onRefreshList: () => Promise<void>;
}

export interface ITaskDataRowDialogState {
    canUpdateTask: boolean;
    id: string;
    rowId: string;
    isNew: boolean;
    item: ITaskDataRow;
    orgCols: ITaskDataRowCol[];
    cols: ITaskDataRowCol[];
    attachments: IFileAttachment[];
    createUser: IModifyUser;
    modifyUser: IModifyUser;
    lockedUser: IModifyUser;
    isLockedToOtherUser: boolean;
}

export class TaskDataRowDialog extends React.Component<ITaskDataRowDialogProp, ITaskDataRowDialogState> {
    private orgStateHash: string = "";

    constructor(props: ITaskDataRowDialogProp) {
        super(props);
        this.state = {
            canUpdateTask: false,
            item: null,
            id: "",
            rowId: "",
            isNew: false,
            orgCols: [],
            cols: [],
            attachments: [],
            createUser: null,
            modifyUser: null,
            lockedUser: null,
            isLockedToOtherUser: false,
        };
    }

    loadData = async (id: string) => {
        const props = this.props;
        //Unlock current task
        if (this.state.item && this.state.canUpdateTask && LockedRecord.isLockedToUser(this.state.lockedUser, props.sessionState.code)) {
            await recordLockManager.unlock(props.companyId, this.state.id, DatabaseRecordType.TaskDataRow);
        }
        //Load and lock task
        let item: ITaskDataRow = null;
        let canUpdateTask = true;
        if (id !== Base.emptyGuid) {
            const result = await AppUtils.callService(() => taskService.getTaskDataRowEdit(props.companyId, props.taskId, id), false);
            if (!result) return;
            item = result.item;
            item.initializeCols(props.taskType);
            canUpdateTask = result.canUpdateTask;
        } else {
            const result = await AppUtils.callService(() => taskService.getTaskDataRowDefaultValues(props.companyId, props.taskId), false);
            if (!result) return;
            item = new TaskDataRow();
            item.id = Base.getGuid();
            item.initializeToTaskType(props.taskType, result.cols);
        }
        const isNew = item.isNew();
        const state = {
            ...this.state,
            canUpdateTask,
            item,
            id: item.id,
            rowId: item.rowId,
            isNew,
            orgCols: item.cols.map(i => new TaskDataRowCol(i)),
            cols: item.cols,
            attachments: item.attachments,
            createUser: ModifyUser.createModifyUser(item.createUserCode, item.createUserName, item.createDate, item.createUserPicture),
            modifyUser: ModifyUser.createModifyUser(item.modifyUserCode, item.modifyUserName, item.modifyDate, item.modifyUserPicture),
            lockedUser: item.lockedUserCode
                ? ModifyUser.createModifyUser(item.lockedUserCode, item.lockedUserName, item.lockedDate, item.lockedUserPicture)
                : null,
            isLockedToOtherUser: item.isLockedToOtherUser(props.sessionState.code),
        };
        this.setState(state);
        this.orgStateHash = this.getSaveDataFromState(this.props, state).hash;
        //Add locked record to idb
        if (!isNew && LockedRecord.isLockedToUser(state.lockedUser, props.sessionState.code)) {
            recordLockManager.addLock(LockedRecord.createLockedRecord(props.companyId, state.id, props.sessionState.code, DatabaseRecordType.TaskDataRow));
        }
    };

    componentDidMount(): void {
        this.loadData(this.props.id);
    }

    async componentWillUnmount(): Promise<void> {
        const props = this.props;
        const state = this.state;
        if (!state.item) return;
        if (!state.isNew && this.state.canUpdateTask && LockedRecord.isLockedToUser(state.lockedUser, props.sessionState.code)) {
            await recordLockManager.unlock(props.companyId, state.id, DatabaseRecordType.TaskDataRow);
        }
    }

    async componentDidUpdate(prevProps: ITaskDataRowDialogProp, prevState: ITaskDataRowDialogState): Promise<void> {
        const props = this.props;
        const state = this.state;
        if (prevProps.companyId === props.companyId && prevProps.id === props.id) return;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            if (!await this.saveToDb()) return;
            props.onRefreshList();
        }
        this.loadData(props.id);
    }

    // #region Attachments
    handleAttachmentDownload = async (id: string) => {
        if (!id) return;
        const attachment = this.state.attachments.find(i => i.id === id);
        if (!attachment) return;
        if (attachment.file) {
            downloadjs(attachment.file, attachment.name);
            return;
        }
        if (attachment.isNew()) return;
        const fileName = await AppUtils.callService(() => attachmentService.getAttachment(id));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.AttachmentParameterDownloaded, fileName));
    };

    getRemoveAttachmentsSaveData = (attachmentIds: string[]): SaveDataObj => {
        const props = this.props;
        const result = new SaveDataObj();
        result.append("id", props.taskId);
        result.append("companyId", props.companyId);
        result.append("ids", attachmentIds);
        return result;
    };

    handleAttachmentRemove = async (id: string) => {
        const state = this.state;
        if (!id) return;
        const saveData = this.getRemoveAttachmentsSaveData([id]);
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveAttachmentParameter, state.attachments.find(x => x.id === id)?.name), false) === ConfirmationDialogResult.No) return;
        const result = AppUtils.callService(() => taskService.taskEditRemoveAttachments(saveData.obj));
        if (!result) return;
        this.setState({ attachments: state.attachments.filter(i => i.id !== id) });
    };

    getAddAttachmentsSaveData = async (attachments: IFileAttachment[]): Promise<SaveDataObj> => {
        const props = this.props;
        const state = this.state;
        const result = new SaveDataObj();
        result.append("id", props.taskId);
        result.append("companyId", props.companyId);
        result.append("subOwnerId", state.id);
        result.append("attachments", await SaveAttachmentBase64.fromFileAttachments(attachments));
        return result;
    };
    
    handleAttachmentsAdd = async (fileList: FileList) => {
        const state = this.state;
        const attachments = await FileAttachment.addFileListToFileAttachments([], fileList, 0);
        if (attachments.length < 1) return;
        const saveData = await this.getAddAttachmentsSaveData(attachments);
        const result = await AppUtils.callService(() => taskService.taskEditAddAttachments(saveData.obj));
        if (!result) return;
        EditItem.setItemIdsFromIds(attachments, result.ids);
        this.setState({ attachments: state.attachments.concat(attachments) });
    };
    // #endregion Attachments

    // #region Locking
    handleUnlockAndLockToMe = async () => {
        const props = this.props;
        if (await AppUtils.showConfirmationDialog(Translations.DoYouReallyWantToUnlockTaskDataRow, false) !== ConfirmationDialogResult.Yes) {
            return;
        }
        const result = await recordLockManager.lock(props.companyId, props.id, props.sessionState.code, DatabaseRecordType.TaskDataRow);
        if (!result || result.items.length < 1) return;
        this.setState({
            lockedUser: StoreUtils.getNowModifyUser(result.items[0].lockedDate),
            isLockedToOtherUser: false
        });
    };
    // #endregion Locking
    
    getSaveDataFromState = (props: ITaskDataRowDialogProp, state: ITaskDataRowDialogState): SaveDataObj => {
        const saveTaskDataRow = SaveTaskDataRow.createSaveTaskDataRow(props.taskType.formCols, state.item, state.cols);
        saveTaskDataRow.id = state.id;
        saveTaskDataRow.rowId = state.rowId;
        const result = new SaveDataObj();
        result.append("id", state.id);
        result.append("taskId", props.taskId);
        result.append("companyId", props.companyId);
        result.append("taskDataRow", JSON.stringify(saveTaskDataRow));
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const props = this.props;
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            TaskTypeCol.validateInputOfRequiredTaskTypeCols(props.taskType.formCols, state.cols, null,
                (typeCol: ITaskTypeCol) => {
                    AppUtils.showErrorMessage(String.format(Translations.FieldParameterValueMustBeDefined, typeCol.title));
                    return resolve(false);
                });
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<SaveSuccess> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        const saveData = this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => taskService.saveTaskDataRowEdit(saveData.obj));
        if (!result) return null;
        //Update state and hash
        const stateChange = { id: result.id, rowId: result.rowId };
        this.orgStateHash = (await this.getSaveDataFromState(props, { ...state, ...stateChange })).hash;
        this.setState(stateChange);
        return result;
    };

    handleSave = async (closeDialog: boolean) => {
        const result = await this.saveToDb();
        if (!result) return;
        if (!closeDialog) {
            this.loadData(result.id);
            this.props.onSaved();
            return;
        }
        this.props.onClose(true);
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (state.isNew) return;
        if (await AppUtils.showConfirmationDialog(Translations.DoYouReallyWantToRemoveTaskDataRow, false) === ConfirmationDialogResult.Yes) {
            // Call server
            const result = await props.onRemove(state.id);
            if (!result) return;
            props.onClose(true);
        }
    };

    handlePrint = async () => {
        const props = this.props;
        const state = this.state;
        if (state.isNew) return;
        props.onPrint();
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onClose(false);
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut(this.getSaveDataFromState(props, this.state).hash, this.orgStateHash,
            async () => !!(await this.saveToDb()),
            props.onRefreshList,
            (newStateHash) => { this.orgStateHash = newStateHash; }
        );
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.item) return null;
        const isNew = state.isNew;
        const isReadOnly = props.isReadOnly || state.isLockedToOtherUser;
        const formIsLocked = !isNew && props.taskType.taskLockingType === TaskLockingType.LockSavedForm;
        return (
            <Dialog
                id="taskDataRowDialog"
                maxWidth="lg"
                title={Translations.RowInformation}
                body={
                    <Box display="flex" flexDirection="column">
                        <TaskDynamicFormContainer>
                            <TaskDynamicForm
                                isReadOnly={isReadOnly || formIsLocked}
                                isEdit={!isNew}
                                taskTypeCols={props.taskType.formCols}
                                taskDataRowCols={state.cols}
                                taskDataRowColsOrg={state.orgCols}
                                onRefreshCols={(cols: ITaskDataRowCol[]) => this.setState({ cols })}
                            />
                        </TaskDynamicFormContainer>
                        {!isNew && ((!isReadOnly && !formIsLocked) || state.attachments.length > 0) &&
                            <AttachmentList
                                isReadOnly={isReadOnly}
                                title={<label>{Translations.Attachments}</label>}
                                attachments={state.attachments}
                                margin={"0.5rem 0 0 0"}
                                onRemoveAttachment={this.handleAttachmentRemove}
                                onDownloadAttachment={this.handleAttachmentDownload}
                                onAddAttachments={this.handleAttachmentsAdd}
                            />
                        }
                        {isNew &&
                            <Alert severity="info">{Translations.TaskDataRowMustBeSavedBeforeAttachmentsCanBeAdded}</Alert>
                        }
                    </Box>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={this.handlePrevious}
                            onNext={this.handleNext}
                            createUser={!isNew ? state.createUser : null}
                            modifyUser={!isNew ? state.modifyUser : null}
                            lockedUser={state.lockedUser}
                            isLockedToOtherUser={!isNew && state.isLockedToOtherUser}
                            onUnlockAndLockToMe={props.sessionState.isSuperAdmin ? this.handleUnlockAndLockToMe : null}

                        />
                        <DialogButtons
                            isNew={isNew}
                            removeDisabled={isReadOnly}
                            saveDisabled={isReadOnly || formIsLocked}
                            onCancel={this.handleCancel}
                            onPrint={!isNew ? this.handlePrint : null}
                            onRemove={!isNew ? this.handleRemove : null}
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}
            />
        );
    }
}